﻿import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import TKFilter, { FilterItem, FilterType } from '@tk/components/tk.filter';
import TKFilterGroup from '@tk/components/tk.filter.group';

interface FormDataEntry {
    key: string;
    value: string;
}

export default class TKFilterDropdown extends TKCustomElementFactory {
    tkFilter?: TKFilter;
    tkFilterGroup?: TKFilterGroup;
    filterItem?: FilterItem;
    filterForm?: HTMLFormElement;
    applyButton?: HTMLButtonElement;
    counterElement?: HTMLElement;
    hasItemsClassName: string;

    filterCollection?: FilterItem[];

    constructor() {
        super();

        this.tkFilter = this.closest('[data-tk-filter-wrapper]') || undefined;
        this.tkFilterGroup = this.closest('[data-tk-filter-group]') || undefined;
        this.filterForm = this.querySelector('form') || undefined;
        this.applyButton = this.querySelector('[data-tk-filter-apply]') || undefined;
        this.counterElement = this.querySelector('[data-tk-filter-dropdown-counter]') || undefined;
        this.hasItemsClassName = this.getAttribute('data-tk-has-items-class-name') || 'tk-filter-dropdown--has-items';
    }

    connectedCallback(): void {
        if (
            !this.dataset.tkFilterDropdownId ||
            !this.dataset.tkFilterDropdownAttribute ||
            !this.filterForm ||
            !this.applyButton
        )
            return;
        if (this.checkFilterItems()) this.setupFilterList();
        this.registerListener();
        this.registerFilterListener();
    }

    registerFilterListener() {
        this.tkFilter?.subscribe((data) => {
            if (data.action !== 'REFRESHED-DRILLDOWN' || !this.counterElement) return;
            const filteredList = data.filterList.filter(
                (item) => item.id === this.getAttribute('data-tk-filter-dropdown-id'),
            );
            this.counterElement.textContent = String(filteredList.length);
            this.classList.toggle(this.hasItemsClassName, filteredList.length > 0);
        });
    }

    registerListener(): void {
        const handleFilterSubmit = this.performFilter.bind(this);
        this.pushListener({ event: 'submit', element: this.filterForm!, action: handleFilterSubmit });

        const handleFilterApply = this.applyFilterItem.bind(this);
        this.pushListener({ event: 'click', element: this.applyButton!, action: handleFilterApply });
    }

    setupFilterList() {
        const data = this.getFormData();
        data.length > 0 && this.addFilterItems(data);
    }

    applyFilterItem() {
        const data = this.getFormData();

        if (data.length > 0) {
            this.addFilterItems(data);
        } else {
            this.tkFilter?.deleteAllByProp(
                this.tkFilter?.type === FilterType.RD_FILTER
                    ? this.dataset.tkFilterDropdownId!
                    : this.dataset.tkFilterDropdownAttribute!,
            );
        }

        this.tkFilter?.refreshDrilldown();
    }

    performFilter(event: SubmitEvent) {
        event.preventDefault();
        const data = this.getFormData();

        if (data.length > 0) {
            this.addFilterItems(data);
        } else {
            this.tkFilter?.deleteAllByProp(
                this.tkFilter?.type === FilterType.RD_FILTER
                    ? this.dataset.tkFilterDropdownId!
                    : this.dataset.tkFilterDropdownAttribute!,
            );
        }

        this.tkFilter?.post();
    }

    protected getFormData(): FormDataEntry[] {
        const formData = new FormData(this.filterForm);
        const fieldElements = this.filterForm?.querySelectorAll('input, select');
        if (!fieldElements) return [];
        const ignoreFieldElements = Array.from(fieldElements).filter(
            (element) =>
                element.hasAttribute('data-tk-ignore-field') && !element.closest('[data-tk-filter-dropdown-is-range]'),
        );
        const ignoreFieldNames = ignoreFieldElements.map((field) => field.getAttribute('name') || '');
        ignoreFieldNames.forEach((name) => formData.delete(name));
        const dataList = Array.from(formData.entries());
        return dataList
            .filter((item) => item[1].toString() !== '')
            .map((item) => {
                const value = item[1].toString();
                return { key: item[0], value } as FormDataEntry;
            });
    }

    protected checkFilterItems() {
        const data = this.getFormData();
        return Object.keys(data).length > 0;
    }

    protected addFilterItems(data: FormDataEntry[]) {
        this.clearFilterCollection();

        data.forEach((item) => {
            this.filterCollection!.push({
                id: this.dataset.tkFilterDropdownId!,
                attr: this.dataset.tkFilterDropdownAttribute!,
                value: item.value,
            });
        });

        this.tkFilter?.add(this.filterCollection!, this.hasAttribute('data-tk-filter-dropdown-is-range'));
    }

    protected clearFilterCollection() {
        this.filterCollection = [];
    }
}
